import { useEffect } from 'react';
import { Device as CapDevice } from '@capacitor/device';
import { post, put } from './apiService';
import { logError, logDebug, logTrace } from './loggerService';
import { getPushPermissionState } from './platformService';
import { useActivityStore } from '../store/useActivityStore';
import type { Device } from '../types/device';

// Mirrors pushService's flag (not imported: pushService imports this module).
const PUSH_DISABLED = import.meta.env.VITE_FIREBASE_PUSH_DISABLED === 'true';

// In-memory only. Was previously persisted to `hp_device` in localStorage so
// that subsequent boots could replay the last-known userId + pushToken before
// auth resolved; we now rely on the BE's merge semantics on PUT /device
// (anonymous and authenticated paths both preserve fields the FE omits) plus
// the FCM SDK's own persistent token, so there's nothing the FE meaningfully
// needs to remember across cold starts. See
// docs/cold-start-and-localStorage-cache.md for the full timeline.
let currentDevice: Device | null = null;
let deviceReadyResolve: () => void;
const deviceReady = new Promise<void>((resolve) => { deviceReadyResolve = resolve; });

/**
 * Collect device info from Capacitor Device plugin. Stores in `currentDevice`
 * and resolves `deviceReady`. Does NOT fire PUT /device — the first PUT
 * happens later via `putDeviceNow`, scheduled by authService after the
 * restore-session flow resolves (so it goes out with userId attached when
 * the user is authenticated).
 *
 * Failure: logs and leaves `currentDevice` null. `getDevice()` returns null
 * and `setDeviceUserId` / `setDevicePushToken` early-return. Capacitor's
 * native device probe is highly reliable; the catch is belt-and-suspenders.
 */
async function collectDevice(): Promise<void> {
  try {
    const [idResult, infoResult] = await Promise.all([
      CapDevice.getId(),
      CapDevice.getInfo(),
    ]);

    currentDevice = {
      identifier: idResult.identifier,
      name: infoResult.name ?? null,
      model: infoResult.model ?? null,
      platform: infoResult.platform as Device['platform'],
      operatingSystem: infoResult.operatingSystem,
      osVersion: infoResult.osVersion,
      iOSVersion: infoResult.iOSVersion ?? null,
      androidSDKVersion: infoResult.androidSDKVersion ?? null,
      manufacturer: infoResult.manufacturer,
      isVirtual: infoResult.isVirtual,
      webViewVersion: infoResult.webViewVersion ?? null,
      appVersion: import.meta.env.VITE_VERSION || 'unknown',
      // userId + pushToken are populated later by setDeviceUserId /
      // setDevicePushToken once whoami resolves / push permission grants.
    };

    logTrace('Device info collected', undefined, currentDevice);
    deviceReadyResolve();
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'Failed to collect device info',
    );
  }
}

/**
 * PUT /device with whatever state `currentDevice` currently holds. Safe to
 * call repeatedly: the BE upsert handler merges fields the payload omits,
 * so an early-boot PUT with no userId won't clobber a previously-claimed
 * userId on the BE row, and a PUT with no pushToken won't wipe an existing
 * one. The only path that destructively clears `user_id` is the explicit
 * POST /device/logout endpoint (see `logoutDeviceOnBackend`).
 *
 * Every PUT carries the device's notification permission, read fresh: the
 * user can turn it off in the OS settings at any time, and the backend then
 * clears this device's stored push token instead of sending to a phone that
 * can't show it. Left out when push is disabled for this build, so a dev
 * build doesn't wipe a token it simply never asked for.
 *
 * Caller doesn't need to await unless they want to sequence subsequent
 * work behind it — failures are swallowed with a log, never thrown.
 * Resolves true when the PUT succeeded.
 */
export async function putDeviceNow(): Promise<boolean> {
  // Wait for InitDevice's Capacitor probe to finish before sending. The
  // typical race: authService kicks off whoami → eventually calls
  // putDeviceNow, while InitDevice is still mid-Capacitor-call. Without this
  // gate we'd no-op the boot PUT.
  await deviceReady;
  if (!currentDevice) return false;
  if (!PUSH_DISABLED) {
    currentDevice.pushPermission = getPushPermissionState();
  }
  try {
    await put<void>('/device', currentDevice);
    logDebug('Device registered with backend');
    return true;
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'Failed to PUT device',
    );
    return false;
  }
}

/**
 * Update the in-memory device's `userId`. Does NOT fire PUT — the caller is
 * expected to follow up with `putDeviceNow` (or for the cold-boot path,
 * `whoAmI` invokes both in a single Promise.all). Login flows that already
 * called `whoAmI` get this update via whoami's own setDeviceUserId call;
 * subsequent explicit calls are harmless no-ops if the value matches.
 */
export async function setDeviceUserId(userId: string): Promise<void> {
  await deviceReady;
  if (!currentDevice) return;
  currentDevice.userId = userId;
  logDebug('Device userId set (in-memory)', undefined, {
    identifier: currentDevice.identifier,
    userId,
  });
}

/**
 * Disassociate this device's row from the current user via the explicit
 * `POST /device/logout` endpoint. Called from `authService.logout`.
 *
 * Why an endpoint instead of "PUT /device with userId omitted":
 *   PUT /device is non-destructive — it preserves fields the FE omits.
 *   That means there is no payload shape that expresses "log me out" via
 *   PUT. The explicit endpoint also makes the intent visible in BE logs
 *   and metrics, which the previous implicit-PUT pattern hid.
 *
 * Push token is preserved on the BE row (hardware-bound, not user-bound).
 * The next user who logs in here will claim the now-unassociated row via
 * the BE's `GetDeviceByDeviceIDUnassociated` path and inherit that token.
 *
 * Best-effort: swallows errors. The page is full-reloading right after
 * this returns anyway, so a transient failure shouldn't block logout.
 */
export async function logoutDeviceOnBackend(): Promise<void> {
  await deviceReady;
  if (!currentDevice) return;
  try {
    await post<void>('/device/logout', { identifier: currentDevice.identifier });
    logDebug('Device disassociated from user via POST /device/logout');
  } catch (error) {
    logError(
      error instanceof Error ? error : new Error(String(error)),
      'Failed to POST /device/logout',
    );
  }
}

/**
 * Set the push token on the device, then PUT to backend. Called by
 * `pushService` either at boot (when push permission is already granted
 * and the FCM SDK returns the existing token) or mid-session (when the
 * user just granted permission). PUTs immediately because the timing of
 * this call is independent of restoreSession — the boot-time `putDeviceNow`
 * may have already fired before the FCM token was available, so we need
 * a follow-up PUT to deliver it.
 */
export async function setDevicePushToken(pushToken: string): Promise<void> {
  await deviceReady;
  if (!currentDevice) return;

  currentDevice.pushToken = pushToken;
  logDebug('Device pushToken set', undefined, {
    identifier: currentDevice.identifier,
    pushToken: pushToken.substring(0, 20) + '...',
  });
  // The backend has this device's token: with permission granted, this
  // device can receive push (Settings shows push as on only then).
  if (await putDeviceNow()) {
    useActivityStore.getState().setPushTokenRegistered(true);
  }
}

/**
 * Get the current device object.
 */
export function getDevice(): Device | null {
  return currentDevice;
}

/**
 * Returns a promise that resolves once device info has been collected.
 */
export function waitForDevice(): Promise<void> {
  return deviceReady;
}

/**
 * Renderless component that collects device info on mount. Does NOT fire
 * PUT /device on its own — that's scheduled by `authService` after the
 * restore-session flow resolves, so the first PUT goes out with userId
 * attached (when authenticated) and pushToken attached (when push perm
 * was previously granted and InitPush has populated it). See
 * docs/cold-start-and-localStorage-cache.md for the boot timeline.
 */
export function InitDevice() {
  useEffect(() => {
    collectDevice();
  }, []);

  return null;
}
